import React from 'react';

/**
 * ErrorBoundary — catches render/lifecycle errors in its subtree so one broken
 * component shows a recoverable card instead of blanking the ENTIRE app (React
 * unmounts the whole tree on an uncaught render error — that's why a single
 * component fault produced a white screen).
 *
 * It also surfaces the REAL error (name + component stack) to the console, which
 * is invaluable when a minified production build otherwise only reports a coded
 * error like "Minified React error #310".
 *
 * Usage: wrap an area whose failure should be isolated —
 *   <ErrorBoundary label="Dashboard"><MainApp /></ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? ' · ' + this.props.label : ''}]`, error, info && info.componentStack);
    this.setState({ stack: info && info.componentStack });
  }

  render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    const label = this.props.label ? `the ${this.props.label}` : 'this view';
    return (
      <div style={{ padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
        <div
          role="alert"
          style={{
            maxWidth: 680, width: '100%',
            background: 'var(--card, #1a1a1a)',
            border: '1px solid var(--line, #333)',
            borderRadius: 12, padding: 24,
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--text, #eee)' }}>
            ⚠ Something in {label} hit an error
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.5, color: 'var(--muted, #999)' }}>
            The rest of the app is still running — you can switch tabs. Reload to recover this view,
            or copy the details below so it can be fixed.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: 'none', background: 'var(--accent, #3b82f6)', color: '#fff',
              }}
            >
              Reload
            </button>
            <button
              onClick={() => this.setState({ error: null, stack: null })}
              style={{
                fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: '1px solid var(--line, #333)', background: 'transparent', color: 'var(--text, #eee)',
              }}
            >
              Try again
            </button>
          </div>
          <pre
            style={{
              margin: 0, fontSize: 11, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 260, overflow: 'auto', padding: 12, borderRadius: 8,
              background: 'var(--bg, #0d0d0d)', color: 'var(--muted, #888)',
              border: '1px solid var(--line, #333)',
            }}
          >
            {String((error && error.stack) || error)}
            {stack ? '\n\nComponent stack:' + stack : ''}
          </pre>
        </div>
      </div>
    );
  }
}
